/****************************************************************************************/
/* Helper for the FAQ of a derivative (help.json).                                      */
/*                                                                                      */
/* Structurally different from the translations: ONE file for all languages, and an      */
/* ordered list instead of a key map.                                                   */
/*                                                                                      */
/*   { "version": "1.0.4",                                                              */
/*     "questions": [ { "question": {de,en,nl}, "answer": {de,en,nl} } ] }               */
/*                                                                                      */
/* routes/Help/Help.jsx loads it from <cdn>/<className>/help.json. Careful: if the file  */
/* exists it replaces the standard FAQ of the main translation completely.               */
/****************************************************************************************/

/** How many standard questions the portal shows without a help.json. */
export const STANDARD_QUESTIONS = 3;

function normaliseLocalised(value) {
  // Help.jsx also accepts a plain string, then it counts for every language.
  if (typeof value === 'string') return value.trim() ? { '*': value } : {};
  if (!value || typeof value !== 'object') return {};
  const result = {};
  Object.entries(value).forEach(([code, text]) => {
    if (typeof text === 'string') result[code] = text;
  });
  return result;
}

/**
 * @name parseFaq
 * @description Reads a help.json into a shape the editor can work with.
 */
export function parseFaq(json) {
  const questions = Array.isArray(json?.questions) ? json.questions : [];
  return {
    version: typeof json?.version === 'string' ? json.version : null,
    questions: questions.map((entry) => ({
      question: normaliseLocalised(entry?.question),
      answer: normaliseLocalised(entry?.answer),
    })),
  };
}

/** Languages that appear in the file, plus the ones the portal runs. */
export function faqLanguages(faq, portalLanguages = []) {
  const list = [];
  const add = (code) => {
    if (code && code !== '*' && !list.includes(code)) list.push(code);
  };
  portalLanguages.forEach(add);
  faq.questions.forEach((entry) => {
    Object.keys(entry.question).forEach(add);
    Object.keys(entry.answer).forEach(add);
  });
  return list;
}

function filled(value) {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * @name faqEntryIssues
 * @description Which languages of an entry are incomplete, and whether the
 * entry has no question at all.
 */
export function faqEntryIssues(entry, languages) {
  const missing = languages.filter(
    (code) => !filled(entry.question[code]) || !filled(entry.answer[code]),
  );
  const empty = !languages.some((code) => filled(entry.question[code]));
  return { missing, empty };
}

/** Number of entries that are incomplete in at least one language. */
export function countFaqIssues(faq, languages) {
  return faq.questions.filter((entry) => {
    const issues = faqEntryIssues(entry, languages);
    return issues.empty || issues.missing.length > 0;
  }).length;
}

/**
 * @name buildFaqFile
 * @description The file to download. Empty language fields are dropped so the
 * merge request stays readable.
 */
export function buildFaqFile(faq) {
  const dropEmpty = (values) => {
    const result = {};
    Object.entries(values).forEach(([code, text]) => {
      if (filled(text)) result[code] = text;
    });
    return result;
  };

  return {
    ...(faq.version ? { version: faq.version } : {}),
    questions: faq.questions.map((entry) => ({
      question: dropEmpty(entry.question),
      answer: dropEmpty(entry.answer),
    })),
  };
}

/**
 * @name diffFaq
 * @description Compares the loaded FAQ with the draft.
 */
export function diffFaq(base, draft) {
  const added = Math.max(0, draft.questions.length - base.questions.length);
  const removed = Math.max(0, base.questions.length - draft.questions.length);
  const shared = Math.min(base.questions.length, draft.questions.length);
  let edited = 0;
  for (let index = 0; index < shared; index += 1) {
    if (
      JSON.stringify(base.questions[index]) !==
      JSON.stringify(draft.questions[index])
    ) {
      edited += 1;
    }
  }
  return { added, removed, edited, count: added + removed + edited };
}

/** Empty entry for "Frage hinzufügen". */
export function emptyFaqEntry(languages) {
  const fields = {};
  languages.forEach((code) => {
    fields[code] = '';
  });
  return { question: { ...fields }, answer: { ...fields } };
}

/** Moves an entry, used by the up/down buttons. */
export function moveFaqEntry(questions, from, to) {
  if (to < 0 || to >= questions.length) return questions;
  const next = [...questions];
  const [entry] = next.splice(from, 1);
  next.splice(to, 0, entry);
  return next;
}

/**
 * @name buildFaqSummary
 * @description Markdown summary for the merge request description.
 */
export function buildFaqSummary(className, diff, faq) {
  const lines = [
    `FAQ \`${className}\``,
    `Datei: \`derivatives/${className}/help.json\``,
    '',
    `${faq.questions.length} Fragen insgesamt`,
  ];
  if (diff.added) lines.push(`- ${diff.added} neu`);
  if (diff.removed) lines.push(`- ${diff.removed} entfernt`);
  if (diff.edited) lines.push(`- ${diff.edited} geändert`);
  return lines.join('\n');
}
